import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';

// Deduplicate rapid successive auth events to prevent double handling/log spam
let __lastAuthEvent: string | null = null;
let __lastAuthEventTime = 0;

export interface SessionState {
  // Core auth data
  user: User | null;
  session: Session | null;
  
  // Session status flags  
  isInitializing: boolean;
  isValidatingSession: boolean;
  isSigningOut: boolean;
  sessionValidated: boolean;
}

export interface SessionActions {
  // Core session management
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  
  // Sign out functionality
  signOut: () => Promise<void>;
  
  // Initialization and session handling
  initialize: () => Promise<void>;
  handleSessionChange: (event: string, session: Session | null) => Promise<void>;
  
  // Reset functionality
  reset: () => void;
}

export const createSessionActions = (set: any, get: any) => ({
  // Core session setters
  setUser: (user: User | null) => {
    set({ user });
  },
  
  setSession: (session: Session | null) => {
    set({ session });
  },

  // Sign out functionality
  signOut: async () => {
    set({ isSigningOut: true });
    
    try {
      // Prevent DB logging during logout cleanup to avoid 401 spam
      logger.suppressFor(3000);
      logger.info('AuthStore', 'Starting sign out process');
      
      // Clean up Wealthica2 data before logging out
      try {
        const { completeWealthicaCleanup } = await import('@/utils/zustandPersistenceCleanup');
        
        // Complete cleanup - clear both store state and persistence
        await completeWealthicaCleanup();
        
        logger.info('AuthStore', 'Complete Wealthica2 cleanup completed during logout');
      } catch (cleanupError) {
        // Don't fail logout if cleanup fails
        logger.warn('AuthStore', 'Wealthica2 cleanup failed during logout', { 
          error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError)
        });
      }
      
      // Clear any pending OAuth marker before signing out
      get().clearPendingAuthMethod();
      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error('AuthStore', 'Sign out error', { error: error.message });
        throw error;
      }
      
      logger.info('AuthStore', 'Sign out successful');
      
    } catch (error) {
      logger.error('AuthStore', 'Sign out failed', { error });
      set({
        sessionError: error instanceof Error ? error.message : 'Sign out failed',
        isSigningOut: false
      });
      throw error;
    } finally {
      set({ isSigningOut: false });
    }
  },

  // Core initialization
  initialize: async () => {
    // Auth initialization requested - debug logging reduced
    
    const state = get();
    
    // Simple early return - no waiting, no flag setting in guard path
    if (state.isInitializing) {
      logger.debug('AuthStore', 'Auth initialization already in progress - returning early');
      return;
    }

    // Only set flags after deciding to proceed - never set sessionValidated to false
    set({ isInitializing: true, isValidatingSession: true });
    // Starting auth initialization - debug logging reduced
    
    // Safety timeout to prevent infinite "Authenticating..." state
    const initTimeout = setTimeout(() => {
      logger.error('AuthStore', 'Auth initialization timeout - forcing completion');
      set({
        user: null,
        session: null,
        sessionValidated: true, // FIXED: Allow app to proceed to login/auth redirect
        isInitializing: false,
        isValidatingSession: false,
        sessionError: 'Authentication timeout. Please refresh and try again.'
      });
    }, 10000); // 10 second timeout

    try {
      // Starting auth initialization - debug logging reduced

      // Set up auth state change listener
      supabase.auth.onAuthStateChange((event, session) => {
        // Auth state change detected - debug logging reduced
        get().handleSessionChange(event, session);
      });

      // Get initial session from Supabase
      const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        logger.error('AuthStore', 'Error getting initial session', { error: sessionError.message });
        set({ 
          sessionError: sessionError.message,
          isInitializing: false,
          isValidatingSession: false 
        });
        return;
      }

      if (initialSession?.user) {
        logger.debug('AuthStore', 'Found existing session', { userId: initialSession.user.id });
        
        // Check user status first with timeout
        let hasValidProfile = false;
        try {
          const userStatusPromise = get().checkUserStatus(initialSession.user);
          // Slightly longer timeout to reduce false negatives on cold refresh
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('User status check timeout')), 8000)
          );
          hasValidProfile = await Promise.race([userStatusPromise, timeoutPromise]);
        } catch (error: any) {
          const msg = error?.message || String(error);
          logger.warn('AuthStore', 'User status check failed or timed out', { error: msg });
          // IMPORTANT: Treat timeouts as non-fatal to avoid transient redirect flicker
          // Proceed optimistically so we don't clear session on a temporary network hiccup
          if (String(msg).toLowerCase().includes('timeout')) {
            hasValidProfile = true;
          } else {
            hasValidProfile = false;
          }
        }
        
        if (!hasValidProfile) {
          logger.info('AuthStore', 'User not authorized or on waitlist during initialization');
          clearTimeout(initTimeout);
          set({
            user: null,
            session: null,
            sessionValidated: true, // FIXED: Allow redirect to /auth on user status check failure
            isInitializing: false,
            isValidatingSession: false
          });
          return;
        }
        
        // Validate the session with timeout
        let isValid = false;
        try {
          const sessionValidationPromise = get().validateSession(initialSession);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Session validation timeout')), 5000)
          );
          isValid = await Promise.race([sessionValidationPromise, timeoutPromise]);
        } catch (error) {
          logger.error('AuthStore', 'Session validation failed or timed out', { error: error.message });
          isValid = false;
        }
        
        if (!isValid) {
          logger.warn('AuthStore', 'Session validation failed, attempting recovery');
          
          // Attempt session recovery with timeout
          let recovered = false;
          try {
            const recoveryPromise = get().recoverSession();
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Session recovery timeout')), 5000)
            );
            recovered = await Promise.race([recoveryPromise, timeoutPromise]);
          } catch (error) {
            logger.error('AuthStore', 'Session recovery failed or timed out', { error: error.message });
            recovered = false;
          }
          
          if (!recovered) {
            logger.error('AuthStore', 'Session recovery failed');
            clearTimeout(initTimeout);
            set({
              sessionError: 'Session expired. Please sign in again.',
              user: null,
              session: null,
              sessionValidated: true, // FIXED: Allow redirect to /auth on session expiry
              isInitializing: false,
              isValidatingSession: false
            });
            return;
          }
          clearTimeout(initTimeout);
          return; // Recovery will set the session
        }

        // Session is valid
        clearTimeout(initTimeout);
        set({
          session: initialSession,
          user: initialSession.user,
          sessionValidated: true,
          sessionError: null,
          isInitializing: false,
          isValidatingSession: false
        });

        // Load auth method for initialized session with timeout
        try {
          const authMethodPromise = get().loadAuthMethod();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Auth method loading timeout')), 3000)
          );
          await Promise.race([authMethodPromise, timeoutPromise]);
        } catch (authMethodError) {
          logger.warn('AuthStore', 'Failed to load auth method on init', { 
            error: authMethodError instanceof Error ? authMethodError.message : String(authMethodError) 
          });
        }

      } else {
        // No existing session found - debug logging reduced
        try {
          set({
            user: null,
            session: null,
            sessionValidated: true, // FIXED: Allow redirect to /auth when no session found
            isInitializing: false,
            isValidatingSession: false
          });
          // Auth initialization completed - debug logging reduced
        } catch (setError) {
          logger.error('AuthStore', 'Error setting auth state after no session', { 
            error: setError instanceof Error ? setError.message : String(setError) 
          });
          throw setError;
        }
      }

      // Clear timeout since initialization completed successfully
      clearTimeout(initTimeout);
      // Auth initialization fully completed - debug logging reduced

    } catch (error) {
      logger.error('AuthStore', 'Auth initialization error', { error });
      clearTimeout(initTimeout);
      set({
        sessionError: error instanceof Error ? error.message : 'Authentication failed',
        isInitializing: false,
        isValidatingSession: false
      });
    } finally {
      // Bulletproof cleanup - always clear flags and set sessionValidated: true
      // Clearing initialization flags - debug logging reduced
      try {
        set({ 
          isInitializing: false, 
          isValidatingSession: false,
          sessionValidated: true
        });
        // Initialization flags cleared - debug logging reduced
      } catch (finallyError) {
        logger.error('AuthStore', 'Failed to clear initialization flags in finally block', { 
          error: finallyError instanceof Error ? finallyError.message : String(finallyError) 
        });
      }
    }
  },

  // Handle session changes from onAuthStateChange
  handleSessionChange: async (event: string, newSession: Session | null) => {
    // Auth state changed - debug logging reduced

    // Suppress duplicate events fired in quick succession (e.g., SIGNED_IN twice)
    const __now = Date.now();
    if (__lastAuthEvent === event && (__now - __lastAuthEventTime) < 1000) {
      logger.debug('AuthStore', 'Duplicate auth event suppressed', { event });
      return;
    }
    __lastAuthEvent = event;
    __lastAuthEventTime = __now;

    if (event === 'SIGNED_OUT') {
      set({
        user: null,
        session: null,
        sessionValidated: true, // FIXED: Allow redirect to /auth on SIGNED_OUT event
        sessionError: null
      });
    } else if (event === 'TOKEN_REFRESHED' && newSession) {
      logger.debug('AuthStore', 'Token refreshed, updating session');
      set({
        session: newSession,
        user: newSession.user,
        sessionValidated: true
      });
    } else if (event === 'SIGNED_IN' && newSession?.user) {
      logger.debug('AuthStore', 'User signed in, checking user status');
      
      // Check user status (profile vs waitlist)
      const hasValidProfile = await get().checkUserStatus(newSession.user);
      
      if (!hasValidProfile) {
        logger.info('AuthStore', 'User not authorized or on waitlist');
        return; // User will be signed out in checkUserStatus if needed
      }
      
      // Immediately update store with new session/user so route guards see authenticated state
      set({
        session: newSession,
        user: newSession.user,
      });
      
      // Handle pending auth method for OAuth flows
      try {
        await get().handlePendingAuthMethod();
      } catch (authMethodError) {
        logger.warn('AuthStore', 'Failed to handle pending auth method on sign in', { 
          error: authMethodError instanceof Error ? authMethodError.message : String(authMethodError) 
        });
      }

      // Update session state
      set({
        sessionValidated: true,
        sessionError: null,
        isInitializing: false,
        isValidatingSession: false
      });

      // Load auth method for new session
      try {
        await get().loadAuthMethod();
      } catch (authMethodError) {
        logger.warn('AuthStore', 'Failed to load auth method on sign in', { 
          error: authMethodError instanceof Error ? authMethodError.message : String(authMethodError) 
        });
      }

      // DISABLED: Auto-restoration causes internal server error during callback processing
      // The connect() call during callback processing interferes with the OAuth flow
      // Keep disabled to prevent server errors and infinite loops on page refresh
      logger.debug('AuthStore', 'Auto-restoration disabled to prevent callback interference');
    }
  },

  // Reset session state
  reset: () => {
    set({
      user: null,
      session: null,
      isInitializing: true,
      isValidatingSession: false,
      isSigningOut: false,
      sessionValidated: true // FIXED: Allow proper initialization flow in reset
    });
  }
});